/**
 * The Stripe-native 7-day free trial's entitlements — the single source of
 * truth for both the dashboard's enforcement (resolveTrialLimits and the
 * remaining constants, re-exported from
 * apps/dashboard/lib/billing/credit-pack.ts) and the marketing/pricing
 * display copy.
 *
 * Client-safe by design: this module must stay import-free (no keys(), no
 * server-only, no stripe) so it can be rendered anywhere.
 *
 * NOT to be confused with `TIER_LIMITS[SubscriptionTier.TRIAL]` in
 * limits-config.ts — that is the unrelated, grandfathered $0.99/mo legacy
 * tier whose values feed real organization limits. Do not merge the two.
 */
const TRIAL_MAX_VIDEOS = 2;
export const TRIAL_MAX_VIDEO_LENGTH_SECONDS = 180;
const TRIAL_MAX_EXPORTS = 1;
const TRIAL_MAX_VOICEOVER_MINUTES = 10;
const TRIAL_MAX_TRANSLATIONS = 1;
/** Script regeneration is excluded from the trial entirely. */
export const TRIAL_MAX_REGENERATIONS = 0;

/** A trialing org's effective caps, after admin overrides. */
export type TrialLimits = {
  maxVideos: number;
  maxVoiceOverMinutes: number;
  maxExports: number;
  maxTranslations: number;
};

/**
 * Resolves the effective trial caps from an organizationLimits row. Each
 * admin override (`trialMax*`, null = unset) replaces the default outright.
 * Voice-over is the exception: it is still clamped to the tier max, because
 * incrementUsage stops the org counter there, so a higher cap would never
 * run out.
 */
export function resolveTrialLimits(row: {
  maxVoiceOverMinutes: number | null;
  trialMaxVideos?: number | null;
  trialMaxVoiceOverMinutes?: number | null;
  trialMaxExports?: number | null;
  trialMaxTranslations?: number | null;
}): TrialLimits {
  return {
    maxVideos: row.trialMaxVideos ?? TRIAL_MAX_VIDEOS,
    maxVoiceOverMinutes: Math.min(
      row.trialMaxVoiceOverMinutes ?? TRIAL_MAX_VOICEOVER_MINUTES,
      row.maxVoiceOverMinutes ?? 0
    ),
    maxExports: row.trialMaxExports ?? TRIAL_MAX_EXPORTS,
    maxTranslations: row.trialMaxTranslations ?? TRIAL_MAX_TRANSLATIONS
  };
}

/** `plural(2, 'video')` → "2 videos". */
export function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

/** One-line summary used adjacent to trial CTAs. */
export const TRIAL_INCLUDES_SUMMARY =
  '2 videos (3 min each), 10 mins of standard AI voice-over, 1 translation, and 1 watermarked export';

export const TRIAL_UNLOCK_NOTE =
  'Full plan limits unlock when your paid plan starts.';

export const TRIAL_FAQ_QUESTION = "What's included in the 7-day free trial?";
export const TRIAL_FAQ_ANSWER =
  'The trial gives you the full workflow with capped usage: 2 videos (up to 3 minutes each), 10 minutes of standard-quality AI voice-over, 1 translation, and 1 export (with a watermark). High-quality AI voices, AI avatars, script regenerations, custom templates and credit packs are not available during the trial. Your plan’s full limits — including high-quality voices, watermark-free exports and avatars — unlock the moment your paid plan starts, or immediately if you upgrade early.';
